import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Slider,
    CircularProgress
} from '@mui/material';
import { asyncFetchStoriesByCompoundId, resetStories} from '../../../../store/Vanlifer/Community/storySlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import StoryCardCompact from '../StoryCardCompact';
import { useFetchStories } from '../useFetchStories';
import StoriesHeaderControlsDiscovery from "./StoriesHeaderControlDiscover";
import Rating from "@mui/material/Rating";

interface StoriesQuickOverviewProps {
    compoundId: number;
    showSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

export default function StoriesQuickOverview({
                                                 compoundId,
                                                 showSnackbar,
                                             }: StoriesQuickOverviewProps) {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(state => state.login.isAuthenticated);
    const communityName = useAppSelector(state => state.user.user.communityName);
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [sliderValue, setSliderValue] = useState<[number, number]>([0.5, 5]);
    const [debouncedSliderValue, setDebouncedSliderValue] = useState<[number, number]>([0.5, 5]);

    const { stories, loading, error, nextCursorId, nextCursorDate } = useAppSelector(state => state.story);
    const { bottomRef, isFetchingMore } = useFetchStories({
        nextCursorId,
        nextCursorDate,
        loading,
        error,
        limit: 10,
        fetchAction: asyncFetchStoriesByCompoundId,
        fetchParams: {
            compoundId: compoundId,
            sort: sortDirection,
            minRating: debouncedSliderValue[0],
            maxRating: debouncedSliderValue[1],
        },
    });

    const storiesLoaded = !loading || stories.length > 0;

    const sortedStories = [...stories].sort((a, b) => {
        if (a.storyDate === b.storyDate) {
            return sortDirection === 'DESC'
                ? b.storyId! - a.storyId!
                : a.storyId! - b.storyId!;
        }
        return sortDirection === 'DESC'
            ? b.storyDate! - a.storyDate!
            : a.storyDate! - b.storyDate!;
    });

    useEffect(() => {
        if (error) {
            showSnackbar('Fehler beim Laden der Stories', 'error');
        }
    }, [error]);

    const isInitialLoad = loading && stories.length === 0;

    useEffect(() => {
        dispatch(resetStories());
        if (isAuthenticated) {
            dispatch(asyncFetchStoriesByCompoundId({
                compoundId: compoundId,
                limit: 10,
                sort: sortDirection,
                minRating: debouncedSliderValue[0],
                maxRating: debouncedSliderValue[1],
            }));
        }
    }, [dispatch, isAuthenticated, sortDirection, debouncedSliderValue]);

    return (
        <>
            <Grid container justifyContent="center">
                <Grid item xs={12}>
                    <Box sx={{ mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, my: 2 }}>
                        <StoriesHeaderControlsDiscovery
                            sortDirection={sortDirection}
                            setSortDirection={setSortDirection}
                            sliderValue={sliderValue}
                            setSliderValue={setSliderValue}
                            setDebouncedSliderValue={setDebouncedSliderValue}
                        />
                    </Box>
                </Grid>
            </Grid>

            <Grid
                container
                spacing={0}
                sx={{ px: 0 }}
                justifyContent="center"
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            mx: 'auto',
                            px: { xs: 2, sm: 3, md: 4 },
                            py: 2,
                        }}
                    >
                        <Grid container spacing={2}>
                            {sortedStories.map(entry => (
                                <Grid item xs={12} sm={6} md={4} key={entry.storyId}>
                                    <StoryCardCompact story={entry} isOwner={entry.owner === communityName} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>

                {!error && storiesLoaded && nextCursorId === null && nextCursorDate === null && (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" mt={1} mb={2}>
                            <Typography color="text.secondary">
                                {stories.length === 0 ? 'Keine Stories vorhanden.' : 'Keine weiteren Stories vorhanden.'}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {(isFetchingMore || isInitialLoad) && (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" mt={2}>
                            <CircularProgress size={32} />
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <div ref={bottomRef} />
                </Grid>
            </Grid>
        </>
    );
};
